import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PencilSimple, ChatCenteredDots, WhatsappLogo, Heartbeat, ArrowLeft,
  Paperclip, PaperPlaneRight, X, CheckCircle, ArrowClockwise, CircleHalf,
} from "@phosphor-icons/react";

const STATUS_META = {
  operante: { color: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]", dot: "bg-[#22C55E]", label: "Operante" },
  instabilidade: { color: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", dot: "bg-[#F59E0B]", label: "Instabilidade" },
  manutencao: { color: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]", dot: "bg-[#DC2626]", label: "Manutenção" },
};

export default function CompanyHelp() {
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState(null);
  const [showStatus, setShowStatus] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newForm, setNewForm] = useState({ subject: "", body: "", attachments: [] });
  const [reply, setReply] = useState({ body: "", attachments: [] });
  const attachRef = useRef(null);
  const replyAttachRef = useRef(null);

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/support/tickets"),
        api.get("/service-status"),
      ]);
      setTickets(t.data);
      setStatus(s.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const readAsBase64 = (file) => new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve({
      filename: file.name, mimetype: file.type || "application/octet-stream",
      data: String(r.result || "").split(",")[1] || "",
    });
    r.readAsDataURL(file);
  });

  const handleAttachments = async (files, target) => {
    const list = Array.from(files || []);
    const big = list.find((f) => f.size > 10 * 1024 * 1024);
    if (big) return toast.error("Arquivo maior que 10MB não permitido");
    const parsed = await Promise.all(list.map(readAsBase64));
    if (target === "new") {
      setNewForm((f) => ({ ...f, attachments: [...f.attachments, ...parsed] }));
    } else {
      setReply((r) => ({ ...r, attachments: [...r.attachments, ...parsed] }));
    }
  };

  const openTicket = async (id) => {
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setActiveTicket(data);
      setReply({ body: "", attachments: [] });
      setOpenView(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const createTicket = async () => {
    if (!newForm.subject.trim()) return toast.error("Assunto obrigatório");
    if (!newForm.body.trim()) return toast.error("Descreva o problema");
    setSaving(true);
    try {
      await api.post("/support/tickets", newForm);
      toast.success("Chamado criado. Aguarde retorno.");
      setNewForm({ subject: "", body: "", attachments: [] });
      setOpenNew(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const sendReply = async () => {
    if (!reply.body.trim() && reply.attachments.length === 0) return;
    setSaving(true);
    try {
      await api.post(`/support/tickets/${activeTicket.id}/messages`, reply);
      setReply({ body: "", attachments: [] });
      const { data } = await api.get(`/support/tickets/${activeTicket.id}`);
      setActiveTicket(data);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const closeTicket = async () => {
    try {
      await api.post(`/support/tickets/${activeTicket.id}/close`);
      toast.success("Chamado finalizado");
      const { data } = await api.get(`/support/tickets/${activeTicket.id}`);
      setActiveTicket(data);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reopenTicket = async () => {
    try {
      await api.post(`/support/tickets/${activeTicket.id}/reopen`);
      toast.success("Chamado reaberto");
      const { data } = await api.get(`/support/tickets/${activeTicket.id}`);
      setActiveTicket(data);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openTix = tickets.filter((t) => t.status === "open");
  const closedTix = tickets.filter((t) => t.status === "closed");
  const waNumber = status?.whatsapp_number || "";
  const meta = status ? STATUS_META[status.overall_status] : STATUS_META.operante;

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Suporte</p>
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Precisa de ajuda?</h1>
        <p className="text-sm text-[#5C5C59] mt-2">Escolha a melhor forma de atendimento para seu caso:</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        <button
          onClick={() => setOpenNew(true)}
          data-testid="help-open-ticket-btn"
          className="text-left p-5 bg-[#0F4C3A] hover:bg-[#0A3629] text-white shadow-brutal transition-colors"
        >
          <div className="flex items-center gap-2 font-bold"><PencilSimple size={18} weight="fill" /> Abrir chamado</div>
          <div className="text-[11px] opacity-80 mt-1">Retorno em até 1 dia útil</div>
        </button>
        <a
          href={waNumber ? `https://wa.me/${waNumber.replace(/\D+/g, "")}` : "#"}
          target={waNumber ? "_blank" : undefined}
          rel="noreferrer"
          onClick={(e) => { if (!waNumber) { e.preventDefault(); toast("Nenhum WhatsApp configurado pelo administrador"); } }}
          data-testid="help-whatsapp-btn"
          className="text-left p-5 bg-[#25D366] hover:bg-[#20BE5A] text-white shadow-brutal transition-colors"
        >
          <div className="flex items-center gap-2 font-bold"><WhatsappLogo size={18} weight="fill" /> Suporte via WhatsApp</div>
          <div className="text-[11px] opacity-90 mt-1">{waNumber ? `Fale conosco: ${waNumber}` : "Em breve"}</div>
        </a>
        <button
          onClick={() => setShowStatus((v) => !v)}
          data-testid="help-status-btn"
          className="text-left p-5 bg-white hover:bg-[#F7F7F5] border border-[#E5E5E0] shadow-brutal transition-colors"
        >
          <div className="flex items-center gap-2 font-bold text-[#0F4C3A]"><Heartbeat size={18} weight="fill" /> Status dos serviços</div>
          <div className="text-[11px] text-[#5C5C59] mt-1">Acompanhe o status e resolução de falhas comuns</div>
        </button>
      </div>

      {showStatus && status && (
        <div className={`mb-8 border p-5 ${meta.color}`} data-testid="help-status-card">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-3 h-3 rounded-full ${meta.dot}`}></span>
            <div className="font-bold uppercase tracking-widest text-xs">Status atual: {meta.label}</div>
          </div>
          {status.message && <p className="text-sm mt-2">{status.message}</p>}
          <p className="text-[10px] opacity-70 mt-2">Atualizado em: {new Date(status.updated_at).toLocaleString("pt-BR")}</p>
        </div>
      )}

      <TicketTable title="Chamados abertos" tickets={openTix} onOpen={openTicket} testId="open" />
      <div className="mt-8">
        <TicketTable title="Chamados concluídos" tickets={closedTix} onOpen={openTicket} testId="closed" />
      </div>

      {/* New Ticket dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="bg-white rounded-none max-w-lg">
          <DialogHeader><DialogTitle>Abrir chamado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Assunto</Label>
              <Input value={newForm.subject} onChange={(e) => setNewForm({ ...newForm, subject: e.target.value })} data-testid="help-new-subject" className="rounded-none mt-1" placeholder="Ex: Problema ao conectar WhatsApp" />
            </div>
            <div>
              <Label className="text-sm">Descrição</Label>
              <Textarea value={newForm.body} onChange={(e) => setNewForm({ ...newForm, body: e.target.value })} data-testid="help-new-body" className="rounded-none mt-1 min-h-[140px]" placeholder="Descreva o que está acontecendo. Emojis e formatação livre. 😊" />
            </div>
            <div>
              <Label className="text-sm">Anexos (opcional, até 10MB cada)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {newForm.attachments.map((a, i) => (
                  <div key={i} className="text-xs bg-[#F7F7F5] px-2 py-1 flex items-center gap-1">
                    <Paperclip size={12} /> {a.filename}
                    <button onClick={() => setNewForm({ ...newForm, attachments: newForm.attachments.filter((_, x) => x !== i) })} className="text-[#DC2626] hover:opacity-70"><X size={12} weight="bold" /></button>
                  </div>
                ))}
                <button onClick={() => attachRef.current?.click()} data-testid="help-new-attach-btn" className="text-xs border border-[#E5E5E0] px-2 py-1 hover:bg-[#F7F7F5]">
                  + Adicionar arquivo
                </button>
                <input ref={attachRef} type="file" multiple className="hidden" onChange={(e) => handleAttachments(e.target.files, "new")} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNew(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={createTicket} disabled={saving} data-testid="help-new-save-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
              {saving ? "Enviando…" : "Enviar chamado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Ticket dialog */}
      <Dialog open={openView} onOpenChange={setOpenView}>
        <DialogContent className="bg-white rounded-none max-w-2xl max-h-[92vh] overflow-y-auto">
          {activeTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <button onClick={() => setOpenView(false)} className="text-[#5C5C59] hover:text-[#0F4C3A]"><ArrowLeft size={16} weight="bold" /></button>
                  {activeTicket.subject}
                  <span className={`ml-2 text-[10px] px-2 py-0.5 border ${activeTicket.status === "open" ? "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" : "bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]"}`}>
                    {activeTicket.status === "open" ? "Aberto" : "Concluído"}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {(activeTicket.messages || []).map((m) => {
                  const isMaster = m.sender_role === "master";
                  return (
                    <div key={m.id} className={`flex ${isMaster ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[85%] p-3 text-sm ${isMaster ? "bg-[#EDF3F0] border border-[#0F4C3A]/20" : "bg-white border border-[#E5E5E0]"}`}>
                        <div className="text-[10px] font-bold text-[#0F4C3A] mb-1">
                          {m.sender_name} {isMaster && "(Suporte)"}
                        </div>
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        {(m.attachments || []).map((a, i) => (
                          <a key={i} href={`data:${a.mimetype};base64,${a.data}`} download={a.filename}
                             className="block mt-2 text-[10px] text-[#0F4C3A] underline break-all">
                            📎 {a.filename}
                          </a>
                        ))}
                        <div className="text-[9px] text-[#5C5C59] mt-1">
                          {new Date(m.created_at).toLocaleString("pt-BR")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {activeTicket.status === "open" ? (
                <div className="border-t border-[#E5E5E0] pt-3 space-y-2">
                  <Textarea value={reply.body} onChange={(e) => setReply({ ...reply, body: e.target.value })} data-testid="help-reply-body" placeholder="Escreva sua resposta…" className="rounded-none min-h-[80px]" />
                  <div className="flex flex-wrap gap-2">
                    {reply.attachments.map((a, i) => (
                      <div key={i} className="text-xs bg-[#F7F7F5] px-2 py-1 flex items-center gap-1">
                        <Paperclip size={12} /> {a.filename}
                        <button onClick={() => setReply({ ...reply, attachments: reply.attachments.filter((_, x) => x !== i) })} className="text-[#DC2626] hover:opacity-70"><X size={12} weight="bold" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <button onClick={() => replyAttachRef.current?.click()} data-testid="help-reply-attach-btn" className="text-xs text-[#0F4C3A] hover:underline">
                      <Paperclip size={12} weight="bold" className="inline mr-1" /> Anexar
                    </button>
                    <input ref={replyAttachRef} type="file" multiple className="hidden" onChange={(e) => handleAttachments(e.target.files, "reply")} />
                    <div className="flex items-center gap-2">
                      <Button variant="outline" onClick={closeTicket} data-testid="help-close-btn" className="rounded-none">
                        <CheckCircle size={14} weight="bold" className="mr-1" /> Concluir
                      </Button>
                      <Button onClick={sendReply} disabled={saving} data-testid="help-reply-send-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                        <PaperPlaneRight size={14} weight="bold" className="mr-1" /> Enviar
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-t border-[#E5E5E0] pt-3 text-center">
                  <Button onClick={reopenTicket} variant="outline" data-testid="help-reopen-btn" className="rounded-none">
                    <ArrowClockwise size={14} weight="bold" className="mr-1" /> Reabrir chamado
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TicketTable({ title, tickets, onOpen, testId }) {
  return (
    <div>
      <h2 className="text-lg font-black tracking-tighter text-[#1A1A1A] mb-3">{title}</h2>
      <div className="bg-white border border-[#E5E5E0] shadow-brutal" data-testid={`help-tix-table-${testId}`}>
        <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 px-4 py-2 bg-[#F7F7F5] border-b border-[#E5E5E0] text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">
          <div>Criado em</div><div>Assunto</div><div>Autor</div><div>Ação</div>
        </div>
        {tickets.length === 0 && (
          <div className="p-4 text-center text-xs text-[#5C5C59]">Nenhum chamado encontrado.</div>
        )}
        {tickets.map((t) => (
          <button key={t.id} onClick={() => onOpen(t.id)} data-testid={`help-tix-row-${t.id}`} className="w-full grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 px-4 py-3 border-b border-[#E5E5E0] last:border-b-0 hover:bg-[#F7F7F5] transition text-left">
            <div className="text-xs text-[#5C5C59]">
              {new Date(t.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
            </div>
            <div className="font-semibold text-sm truncate">{t.subject}</div>
            <div className="text-[10px] text-[#5C5C59]">{t.created_by_name}</div>
            <div className="text-[10px] text-[#0F4C3A] font-semibold">Abrir →</div>
          </button>
        ))}
      </div>
    </div>
  );
}
